// src/pages/AttendanceLanding.jsx

import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import withProjectPermissions from '../components/withProjectPermissions';
import {
  collection,
  getDocs,
  query,
  orderBy,
  where,
  limit,
  doc,
  getDoc,
  startAt,
  endAt,
  onSnapshot
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/firebase';
import DateRangePicker from '../components/DateRangePicker';
import { ArrowLeft } from 'lucide-react';

// Simple Override Tooltip - PM Only (just the key info)
const OverrideTooltip = ({ log, children }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  if (!log || !log.isOverride || log.overrideReason === 'site_host') {
    return children;
  }
  
  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'extreme': return '#dc2626';
      case 'high': return '#ea580c';
      case 'moderate': return '#f59e0b';
      case 'low': return '#eab308';
      case 'technical': return '#6366f1';
      default: return '#b45309';
    }
  };
  
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        style={{ cursor: 'help' }}
      >
        {children}
      </div>
      
      {showTooltip && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: '8px',
          background: 'var(--cc-card-bg)',
          border: '2px solid var(--branding-app-surface-panel-border)',
          borderRadius: '8px',
          padding: '12px',
          minWidth: '200px',
          maxWidth: '250px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          fontSize: '13px',
          lineHeight: '1.4',
          whiteSpace: 'nowrap'
        }}>
          {/* Manual override by PM */}
          {log.isManualOverride && log.performedByName ? (
            <div style={{ fontSize: '13px', color: 'var(--cc-text-muted)' }}>
              Manually {log.type === 'checkIn' ? 'checked in' : 'checked out'} by {log.performedByName}
            </div>
          ) : log.distanceFromSiteFormatted ? (
            <div style={{ 
              fontSize: '14px',
              fontWeight: '600',
              color: getSeverityColor(log.overrideSeverity),
              marginBottom: '4px'
            }}>
              📍 {log.distanceFromSiteFormatted} from site
            </div>
          ) : log.overrideReason && log.overrideReason !== 'manual_by_pm' ? (
            <div style={{ fontSize: '13px', color: 'var(--cc-text-muted)' }}>
              {log.overrideReason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </div>
          ) : (
            <div style={{ fontSize: '13px', color: 'var(--cc-text-muted)' }}>
              Override approved by user
            </div>
          )}
          
          {/* Network status if offline */}
          {log.networkStatus === 'offline' && (
            <div style={{ 
              fontSize: '12px', 
              color: '#dc2626',
              marginTop: '6px',
              fontWeight: '500'
            }}>
              🔴 Offline
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ImageModal = ({ open, onClose, imageUrl, name }) => {
  if (!open || !imageUrl) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(0,0,0,0.35)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'var(--cc-card-bg)',
        borderRadius: 14,
        padding: 0,
        minWidth: 340,
        maxWidth: '90vw',
        maxHeight: '90vh',
        boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        position: 'relative'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 28px 10px 28px',
          borderBottom: '1px solid var(--branding-app-surface)'
        }}>
          <span style={{ fontWeight: 600, fontSize: 18, color: 'var(--cc-text-primary)' }}>{name}</span>
          <button
            onClick={onClose}
            style={{
              background: 'var(--cc-cancel-bg)',
              color: 'var(--cc-text-primary)',
              border: 'none',
              borderRadius: 6,
              padding: '4px 14px',
              fontWeight: 500,
              cursor: 'pointer',
              fontSize: 16,
            }}
          >
            Close
          </button>
        </div>
        <div style={{
          padding: 24,
          overflow: 'auto',
          maxHeight: '70vh',
          minHeight: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <img
            src={imageUrl}
            alt={name}
            style={{
              maxWidth: '90vw',
              maxHeight: '70vh',
              borderRadius: 8,
              border: '1px solid var(--branding-app-surface-panel-border)',
              background: 'var(--branding-app-surface-panel)'
            }}
          />
        </div>
      </div>
    </div>
  );
};

function AttendanceLanding({ permissions }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [historicalRecords, setHistoricalRecords] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [profileModal, setProfileModal] = useState({ open: false, url: null, name: null });
  
  // Historical filter states
  const [viewMode, setViewMode] = useState('current'); // 'current', 'historical', or 'teamReport'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedHistoricalTeam, setSelectedHistoricalTeam] = useState(null);
  
  // Team Report states
  const [teamReportData, setTeamReportData] = useState([]);
  const [reportTimePeriod, setReportTimePeriod] = useState('week'); // 'today', 'week', 'month', 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Real-time listeners
  const [siteAccessUnsubscribe, setSiteAccessUnsubscribe] = useState(null);
  const [workSessionsUnsubscribe, setWorkSessionsUnsubscribe] = useState(null);
  
  // Company name mapping for PMs
  const [pmCompanies, setPmCompanies] = useState({}); // { userId: companyName }

  // Manual check-in/out loading state (uid being actioned)
  const [manualCheckLoading, setManualCheckLoading] = useState(null);

  // Per-user cache for static data (team, profile picture, company name).
  // Avoids re-fetching unchanged users on every Firestore snapshot event.
  const userDataCache = useRef({});

  // "+ Check someone in" — the on-site table now only shows people who are actually on
  // site, so manually checking in someone who's off-site needs its own lookup rather than
  // a button on a row that no longer renders for them.
  const [showCheckInPicker, setShowCheckInPicker] = useState(false);
  const [checkInSearch, setCheckInSearch] = useState('');
  const [checkInRoster, setCheckInRoster] = useState(null); // null = not loaded yet
  const [loadingRoster, setLoadingRoster] = useState(false);
  const checkInPickerRef = useRef(null);

  // Close the "+ Check someone in" popover on an outside click
  useEffect(() => {
    if (!showCheckInPicker) return;
    const handleClickOutside = (e) => {
      if (checkInPickerRef.current && !checkInPickerRef.current.contains(e.target)) {
        setShowCheckInPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCheckInPicker]);

  const isReadOnly = project?.status === 'closed' || !permissions?.canEdit;

  const handleManualCheck = async (targetUserId, action) => {
    if (isReadOnly) return;
    if (!projectId || manualCheckLoading) return;
    setManualCheckLoading(targetUserId);
    try {
      const manualCheckInOut = httpsCallable(functions, 'manualCheckInOut');
      await manualCheckInOut({ projectId, targetUserId, action });
    } catch (err) {
      console.error('Manual check-in/out failed:', err);
      alert(err?.message || 'Failed to update attendance. Please try again.');
    } finally {
      setManualCheckLoading(null);
    }
  };

  const loadCheckInRoster = async () => {
    if (checkInRoster || loadingRoster || !projectId) return;
    setLoadingRoster(true);
    try {
      const roster = [];
      const seen = new Set();

      const currentTeams = teams.length > 0
        ? teams
        : (await getDocs(collection(db, `projects/${projectId}/teams`))).docs.map(d => ({ id: d.id, ...d.data() }));

      await Promise.all(currentTeams.map(async team => {
        const membersSnap = await getDocs(collection(db, `projects/${projectId}/teams/${team.id}/memberDetails`));
        membersSnap.docs.forEach(d => {
          if (seen.has(d.id)) return;
          seen.add(d.id);
          const data = d.data();
          roster.push({ uid: d.id, name: data.name || d.id, context: team.name });
        });
      }));

      if (project) {
        const pmIds = new Set();
        if (project.createdBy) pmIds.add(project.createdBy);
        Object.entries(project.collaborators || {}).forEach(([uid, c]) => {
          if (c?.status === 'accepted' && c?.role === 'pm_collaborator') pmIds.add(uid);
        });
        await Promise.all(Array.from(pmIds).map(async uid => {
          if (seen.has(uid)) return;
          seen.add(uid);
          try {
            const userDoc = await getDoc(doc(db, 'users', uid));
            const data = userDoc.exists() ? userDoc.data() : {};
            const name = [data.firstName, data.lastName].filter(Boolean).join(' ').trim() || uid;
            roster.push({ uid, name, context: 'PM' });
          } catch {
            roster.push({ uid, name: uid, context: 'PM' });
          }
        }));
      }

      roster.sort((a, b) => a.name.localeCompare(b.name));
      setCheckInRoster(roster);
    } catch (err) {
      console.error('Failed to load check-in roster', err);
    } finally {
      setLoadingRoster(false);
    }
  };

  const onSiteUids = new Set(attendanceRecords.map(r => r.uid));
  const checkInCandidates = (checkInRoster || [])
    .filter(p => !onSiteUids.has(p.uid))
    .filter(p => p.name.toLowerCase().includes(checkInSearch.trim().toLowerCase()));

  // Fetch project details
  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) return;
      try {
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        if (projectDoc.exists()) {
          setProject({ id: projectDoc.id, ...projectDoc.data() });
        }
      } catch (error) {
        console.error('Error fetching project:', error);
      }
    };
    fetchProject();
  }, [projectId]);

  // Real-time listener for current attendance data
  useEffect(() => {
    if (!projectId || viewMode !== 'current') return;

    console.log('🔔 Setting up real-time attendance listener for project:', projectId);

    let siteAccessUnsub = null;
    let workSessionsUnsub = null;

    const setupListener = async () => {
      let currentTeams = teams;
      if (currentTeams.length === 0) {
        const teamsSnap = await getDocs(collection(db, `projects/${projectId}/teams`));
        currentTeams = teamsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTeams(currentTeams);
      }

      // Build uid→team reverse map once (O(N_teams) reads, not O(N_users × N_teams))
      const teamMemberMap = {};
      await Promise.all(currentTeams.map(async team => {
        const membersSnap = await getDocs(
          collection(db, `projects/${projectId}/teams/${team.id}/memberDetails`)
        );
        membersSnap.docs.forEach(d => { teamMemberMap[d.id] = team; });
      }));

      // Reset cache when re-subscribing (e.g. viewMode toggle)
      userDataCache.current = {};

      // Only ever returns people who are actually on site right now — the log write and
      // the currentStatus flip happen atomically everywhere now, so this field is trustworthy.
      const siteAccessRef = query(
        collection(db, 'projects', projectId, 'siteAccess'),
        where('currentStatus', '==', true)
      );
      siteAccessUnsub = onSnapshot(siteAccessRef, async (snapshot) => {
        try {
          // Remove deleted/no-longer-on-site users from cache
          snapshot.docChanges()
            .filter(c => c.type === 'removed')
            .forEach(c => { delete userDataCache.current[c.doc.id]; });

          // Only fetch subcollection data for users whose doc actually changed
          const changedUids = snapshot.docChanges()
            .filter(c => c.type !== 'removed')
            .map(c => c.doc.id);

          await Promise.all(changedUids.map(async uid => {
            const cached = userDataCache.current[uid] || {};

            // Name/role/company from the profile, not the log — always populated, unlike a
            // log entry (which can be written without a name if it hadn't loaded client-side
            // yet at check-in time). Only fetched once per user per session.
            if (!cached.profileDataFetched) {
              try {
                const userDoc = await getDoc(doc(db, 'users', uid));
                const userData = userDoc.exists() ? userDoc.data() : {};
                const fullName = [userData.firstName, userData.lastName].filter(Boolean).join(' ').trim();
                cached.name = fullName || uid;
                cached.role = userData.role || null;
                cached.companyId = userData.companyId || null;
              } catch {
                cached.name = uid;
                cached.role = null;
                cached.companyId = null;
              }
              cached.profileDataFetched = true;
            }

            // Always re-fetch for changed users — their attendance just changed. Only need the
            // single most recent log: currentStatus and its causing log are now always written
            // atomically, so if currentStatus is true, that log IS the active check-in.
            const logsSnap = await getDocs(
              query(
                collection(db, 'projects', projectId, 'siteAccess', uid, 'logs'),
                orderBy('timestamp', 'desc'),
                limit(1)
              )
            );
            const logDoc = logsSnap.docs[0];
            if (logDoc) {
              const data = logDoc.data();
              let timestamp;
              if (data.timestamp?.toDate) timestamp = data.timestamp.toDate();
              else if (data.timestamp instanceof Date) timestamp = data.timestamp;
              else if (data.timestamp) timestamp = new Date(data.timestamp);
              else timestamp = new Date();
              cached.signInLog = { id: logDoc.id, ...data, timestamp };
            } else {
              cached.signInLog = null;
            }

            // Static data: only fetch once per user per session
            if (!cached.teamFetched) {
              cached.team = teamMemberMap[uid] || null;
              cached.teamFetched = true;
            }

            if (!cached.profileFetched) {
              try {
                const profileDoc = await getDoc(doc(db, `global_backup/users/profiles/${uid}`));
                cached.profilePictureUrl = profileDoc.exists()
                  ? (profileDoc.data()?.profilePictureUrl || null)
                  : null;
              } catch {
                cached.profilePictureUrl = null;
              }
              cached.profileFetched = true;
            }

            if (cached.role === 'pm' && !cached.companyFetched) {
              try {
                if (cached.companyId) {
                  const companyDoc = await getDoc(doc(db, 'companies', cached.companyId));
                  cached.companyName = companyDoc.exists()
                    ? (companyDoc.data().name || 'Unknown Company')
                    : 'Unknown Company';
                } else {
                  cached.companyName = 'Unknown Company';
                }
              } catch {
                cached.companyName = 'Unknown Company';
              }
              cached.companyFetched = true;
            }

            userDataCache.current[uid] = cached;
          }));

          // Rebuild full records list from all current snapshot docs + cache — everyone here
          // is on site, by construction of the query above.
          const records = snapshot.docs
            .map(userDoc => {
              const uid = userDoc.id;
              const cached = userDataCache.current[uid];
              if (!cached?.profileDataFetched) return null; // Still loading this user

              return {
                uid,
                name: cached.name,
                signInLog: cached.signInLog,
                profilePictureUrl: cached.profilePictureUrl,
                team: cached.team,
                role: cached.role,
                companyId: cached.companyId,
              };
            })
            .filter(r => r !== null)
            .sort((a, b) => {
              const aTime = a.signInLog?.timestamp || new Date(0);
              const bTime = b.signInLog?.timestamp || new Date(0);
              return bTime - aTime;
            });

          setAttendanceRecords(records);

          // Build pmCompanies from cache — no extra reads needed
          const companyMap = {};
          records.filter(r => r.role === 'pm').forEach(r => {
            companyMap[r.uid] = userDataCache.current[r.uid]?.companyName || 'Unknown Company';
          });
          if (Object.keys(companyMap).length > 0) setPmCompanies(companyMap);

          setLoading(false);
        } catch (error) {
          console.error('Error processing real-time attendance update:', error);
        }
      }, (error) => {
        console.error('❌ Error in real-time attendance listener:', error);
      });

      const workSessionsRef = collection(db, 'projects', projectId, 'workSessions');
      workSessionsUnsub = onSnapshot(workSessionsRef, (snapshot) => {
        console.log('📱 Real-time work sessions update received:', snapshot.docs.length, 'sessions');
        if (viewMode === 'teamReport') setRefreshTrigger(prev => prev + 1);
      }, (error) => {
        console.error('❌ Error in real-time work sessions listener:', error);
      });

      setSiteAccessUnsubscribe(() => siteAccessUnsub);
      setWorkSessionsUnsubscribe(() => workSessionsUnsub);
    };

    setupListener();

    return () => {
      console.log('🔇 Cleaning up real-time attendance listeners');
      if (siteAccessUnsub) siteAccessUnsub();
      if (workSessionsUnsub) workSessionsUnsub();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, viewMode]); // teams fetched upfront inside setupListener, not a dep

  // Cleanup listeners when component unmounts or viewMode changes
  useEffect(() => {
    return () => {
      if (siteAccessUnsubscribe) {
        siteAccessUnsubscribe();
      }
      if (workSessionsUnsubscribe) {
        workSessionsUnsubscribe();
      }
    };
  }, [siteAccessUnsubscribe, workSessionsUnsubscribe]);

  // Fetch historical attendance data (no real-time needed for historical)
  useEffect(() => {
    if (!projectId || viewMode !== 'historical') return;

    (async () => {
      setLoading(true);

      try {
        // 1️⃣ Fetch teams first
        const teamsSnap = await getDocs(collection(db, `projects/${projectId}/teams`));
        const teamsData = teamsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTeams(teamsData);

        // 2️⃣ Get all user status docs
        const usersSnap = await getDocs(
          collection(db, 'projects', projectId, 'siteAccess')
        );

        // 3️⃣ Fetch historical logs for each user
        const allHistoricalRecords = [];

        for (const userDoc of usersSnap.docs) {
          const uid = userDoc.id;

          // Resolve name/role from the profile, not the log — always populated, unlike a
          // log entry (which can be written without a name if it hadn't loaded client-side
          // yet at check-in time, or predates the atomic-write fix that backfills auto
          // sign-out logs).
          let profileName = '';
          let profileRole = null;
          try {
            const profileDoc = await getDoc(doc(db, 'users', uid));
            const profileData = profileDoc.exists() ? profileDoc.data() : {};
            profileName = [profileData.firstName, profileData.lastName].filter(Boolean).join(' ').trim();
            profileRole = profileData.role || null;
          } catch {
            // fall through to per-log name/role below
          }

          // Fetch profile picture from global_backup (single source of truth)
          let userTeam = null;
          let profilePictureUrl = null;

          // Get team from memberDetails (for Subs)
          for (const team of teamsData) {
            const memberSnap = await getDoc(doc(db, `projects/${projectId}/teams/${team.id}/memberDetails`, uid));
            if (memberSnap.exists()) {
              userTeam = team;
              break;
            }
          }
          
          // Fetch profile picture from global_backup (works for both PMs and Subs)
          try {
            const globalProfileDoc = await getDoc(doc(db, `global_backup/users/profiles/${uid}`));
            if (globalProfileDoc.exists()) {
              profilePictureUrl = globalProfileDoc.data()?.profilePictureUrl || null;
            }
          } catch (error) {
            console.log(`Could not fetch profile picture for ${uid}:`, error);
          }

          // Get all logs for this user
          let logsQuery = query(
            collection(db, 'projects', projectId, 'siteAccess', uid, 'logs'),
            orderBy('timestamp', 'desc')
          );

          // Add date filters if provided
          if (startDate || endDate) {
            const filters = [];
            if (startDate) {
              const startTimestamp = new Date(startDate + 'T00:00:00');
              filters.push(startAt(startTimestamp));
            }
            if (endDate) {
              const endTimestamp = new Date(endDate + 'T23:59:59');
              filters.push(endAt(endTimestamp));
            }
            logsQuery = query(logsQuery, ...filters);
          }

          const logsSnap = await getDocs(logsQuery);

          // Process each log entry
          logsSnap.docs.forEach(doc => {
            const logData = doc.data();
            let timestamp;
            
            // Handle different timestamp formats
            if (logData.timestamp?.toDate) {
              // Firestore timestamp object
              timestamp = logData.timestamp.toDate();
            } else if (logData.timestamp instanceof Date) {
              // Already a Date object
              timestamp = logData.timestamp;
            } else if (typeof logData.timestamp === 'string') {
              // String timestamp - try to parse it
              timestamp = new Date(logData.timestamp);
            } else if (logData.timestamp) {
              // Fallback for other formats
              timestamp = new Date(logData.timestamp);
            } else {
              timestamp = new Date();
            }
            
            if (timestamp) {
              allHistoricalRecords.push({
                uid,
                name: profileName || logData.name || 'Unknown',
                role: profileRole || logData.role || null,
                type: logData.type,
                timestamp,
                isOverride: !!logData.isOverride,
                profilePictureUrl: profilePictureUrl,  // Already fetched above
                team: userTeam,
                logId: doc.id,
                // GPS Analytics Data
                distanceFromSiteMeters: logData.distanceFromSiteMeters,
                distanceFromSiteFormatted: logData.distanceFromSiteFormatted,
                gpsToleranceMeters: logData.gpsToleranceMeters,
                withinTolerance: logData.withinTolerance,
                overrideReason: logData.overrideReason,
                overrideCategory: logData.overrideCategory,
                overrideSeverity: logData.overrideSeverity,
                toleranceExceededBy: logData.toleranceExceededBy,
                networkStatus: logData.networkStatus,
                gpsAccuracyMeters: logData.gpsAccuracyMeters,
                gpsQuality: logData.gpsQuality,
                locationAgeSeconds: logData.locationAgeSeconds,
                locationAgeCategory: logData.locationAgeCategory
              });
            }
          });
        }

        // 4️⃣ Sort by timestamp (most recent first)
        const sortedRecords = allHistoricalRecords.sort((a, b) => b.timestamp - a.timestamp);
        setHistoricalRecords(sortedRecords);

      } catch (error) {
        console.error('Error fetching historical data:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId, viewMode, startDate, endDate]);

  // Fetch team report data (no real-time needed for reports)
  useEffect(() => {
    if (!projectId || viewMode !== 'teamReport') return;

    (async () => {
      setLoading(true);

      try {
        // 1️⃣ Fetch teams first
        const teamsSnap = await getDocs(collection(db, `projects/${projectId}/teams`));
        const teamsData = teamsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTeams(teamsData);

        // 2️⃣ Fetch work sessions data
        const workSessionsSnap = await getDocs(collection(db, `projects/${projectId}/workSessions`));
        const workSessions = workSessionsSnap.docs.map(doc => {
          const data = doc.data();
          
          // Handle both old timestamp format and new string format for date
          let dateValue;
          if (typeof data.date === 'string') {
            // New format: already a string
            dateValue = data.date;
          } else if (data.date?.toDate) {
            // Old format: Firestore timestamp
            const originalDate = data.date.toDate();
            dateValue = originalDate.toISOString().split('T')[0];
            console.log('🔄 Converting Firestore timestamp:', {
              original: data.date,
              converted: originalDate,
              final: dateValue
            });
          } else if (data.date instanceof Date) {
            // Old format: Date object
            dateValue = data.date.toISOString().split('T')[0];
            console.log('🔄 Converting Date object:', {
              original: data.date,
              final: dateValue
            });
          } else {
            // Fallback
            dateValue = data.date;
            console.log('⚠️ Unknown date format:', data.date);
          }
          
          return {
            id: doc.id,
            ...data,
            checkInTime: data.checkInTime?.toDate ? data.checkInTime.toDate() : new Date(data.checkInTime),
            checkOutTime: data.checkOutTime?.toDate ? data.checkOutTime.toDate() : new Date(data.checkOutTime),
            date: dateValue
          };
        });

        // 3️⃣ Fetch daily hours data
        const dailyHoursSnap = await getDocs(collection(db, `projects/${projectId}/dailyHours`));
        const dailyHours = dailyHoursSnap.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
            lastUpdated: data.lastUpdated?.toDate ? data.lastUpdated.toDate() : new Date(data.lastUpdated)
          };
        });

        // 4️⃣ Process data by teams
        const teamReportData = [];

        for (const team of teamsData) {
          // Get team members
          const memberDetailsSnap = await getDocs(collection(db, `projects/${projectId}/teams/${team.id}/memberDetails`));
          const teamMembers = memberDetailsSnap.docs.map(doc => doc.id);

          // Filter work sessions for this team's members
          const teamWorkSessions = workSessions.filter(session => teamMembers.includes(session.userId));
          const teamDailyHours = dailyHours.filter(hours => teamMembers.includes(hours.userId));

          // Calculate team totals based on time period
          let totalHours = 0;
          let totalSessions = 0;
          let totalOverrideSessions = 0;
          let memberDetails = [];

          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const weekStart = new Date(today);
          weekStart.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          
          // Create todayString using local date components to avoid timezone issues
          const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
          
          // Debug: Show date calculation
          console.log('📅 Date calculation debug:', {
            now: now.toISOString(),
            today: today.toISOString(),
            todayString,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          });

          // Filter by time period
          let filteredSessions = teamWorkSessions;
          let filteredDailyHours = teamDailyHours;

          if (reportTimePeriod === 'today') {
            // todayString is already calculated above using local date components
            
            filteredSessions = teamWorkSessions.filter(session => 
              session.date === todayString
            );
            
            filteredDailyHours = teamDailyHours.filter(hours => 
              hours.date === todayString
            );
            
            // Debug logging for today filter
            console.log('📅 Today filter debug:', {
              todayString,
              totalSessions: teamWorkSessions.length,
              filteredSessions: filteredSessions.length,
              totalDailyHours: teamDailyHours.length,
              filteredDailyHours: filteredDailyHours.length,
              sampleSessionDate: teamWorkSessions[0]?.date,
              sampleDailyHoursDate: teamDailyHours[0]?.date,
              allSessionDates: teamWorkSessions.slice(0, 5).map(s => s.date),
              allDailyHoursDates: teamDailyHours.slice(0, 5).map(h => h.date)
            });
          } else if (reportTimePeriod === 'week') {
            filteredSessions = teamWorkSessions.filter(session => {
              if (!session.date) return false;
              const sessionDate = new Date(session.date);
              return sessionDate >= weekStart;
            });
            filteredDailyHours = teamDailyHours.filter(hours => {
              const hoursDate = new Date(hours.date);
              return hoursDate >= weekStart;
            });
          } else if (reportTimePeriod === 'month') {
            filteredSessions = teamWorkSessions.filter(session => {
              if (!session.date) return false;
              const sessionDate = new Date(session.date);
              return sessionDate >= monthStart;
            });
            filteredDailyHours = teamDailyHours.filter(hours => {
              const hoursDate = new Date(hours.date);
              return hoursDate >= monthStart;
            });
          } else if (reportTimePeriod === 'custom') {
            // Get current values from state for this render
            const currentCustomStartDate = customStartDate;
            const currentCustomEndDate = customEndDate;
            
            if (currentCustomStartDate || currentCustomEndDate) {
              const startDate = currentCustomStartDate ? new Date(currentCustomStartDate) : null;
              const endDate = currentCustomEndDate ? new Date(currentCustomEndDate + 'T23:59:59') : null;
            
            filteredSessions = teamWorkSessions.filter(session => {
              if (!session.date) return false;
              const sessionDate = new Date(session.date);
              if (startDate && endDate) {
                return sessionDate >= startDate && sessionDate <= endDate;
              } else if (startDate) {
                return sessionDate >= startDate;
              } else if (endDate) {
                return sessionDate <= endDate;
              }
              return true;
            });
            
            filteredDailyHours = teamDailyHours.filter(hours => {
              const hoursDate = new Date(hours.date);
              if (startDate && endDate) {
                return hoursDate >= startDate && hoursDate <= endDate;
              } else if (startDate) {
                return hoursDate >= startDate;
              } else if (endDate) {
                return hoursDate <= endDate;
              }
              return true;
            });
            }
          }

          // Calculate totals
          totalHours = filteredDailyHours.reduce((sum, hours) => sum + (hours.totalHours || 0), 0);
          totalSessions = filteredSessions.length; // Count completed work sessions
          totalOverrideSessions = filteredDailyHours.reduce((sum, hours) => sum + (hours.totalOverrideSessions || 0), 0);

          // Get individual member details from memberDetails subcollection
          for (const memberId of teamMembers) {
            const memberSessions = filteredSessions.filter(session => session.userId === memberId);
            const memberDailyHours = filteredDailyHours.filter(hours => hours.userId === memberId);
            
            const memberTotalHours = memberDailyHours.reduce((sum, hours) => sum + (hours.totalHours || 0), 0);
            const memberTotalSessions = memberSessions.length; // Count completed work sessions for this member
            const memberTotalOverrideSessions = memberDailyHours.reduce((sum, hours) => sum + (hours.totalOverrideSessions || 0), 0);

            // Get member data from memberDetails (has name)
            const memberDetailsDoc = await getDoc(doc(db, `projects/${projectId}/teams/${team.id}/memberDetails`, memberId));
            const memberDetailsData = memberDetailsDoc.exists() ? memberDetailsDoc.data() : {};

            // Fetch profile picture from global_backup (single source of truth)
            let profilePictureUrl = null;
            try {
              const globalProfileDoc = await getDoc(doc(db, `global_backup/users/profiles/${memberId}`));
              if (globalProfileDoc.exists()) {
                profilePictureUrl = globalProfileDoc.data()?.profilePictureUrl || null;
              }
            } catch (error) {
              console.log(`Could not fetch profile picture for ${memberId}:`, error);
            }

            memberDetails.push({
              userId: memberId,
              name: memberDetailsData?.name || memberId,
              totalHours: memberTotalHours,
              totalSessions: memberTotalSessions,
              totalOverrideSessions: memberTotalOverrideSessions,
              profilePictureUrl: profilePictureUrl,
              lastSession: memberSessions.length > 0 ? memberSessions[0] : null
            });
          }

          // Sort members by hours worked
          memberDetails.sort((a, b) => b.totalHours - a.totalHours);

          teamReportData.push({
            teamId: team.id,
            teamName: team.name,
            teamColor: team.color || '#e2e8f0',
            totalHours,
            totalSessions,
            totalOverrideSessions,
            memberCount: teamMembers.length,
            memberDetails,
            averageHoursPerMember: teamMembers.length > 0 ? totalHours / teamMembers.length : 0
          });
        }

        // Sort teams by total hours
        teamReportData.sort((a, b) => b.totalHours - a.totalHours);
        setTeamReportData(teamReportData);

      } catch (error) {
        console.error('Error fetching team report data:', error);
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, viewMode, reportTimePeriod, refreshTrigger]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const timestampDate = new Date(timestamp.getFullYear(), timestamp.getMonth(), timestamp.getDate());
    
    const isToday = today.getTime() === timestampDate.getTime();
    
    if (isToday) {
      return `Today at ${timestamp.toLocaleTimeString('en-GB', { 
        hour12: false,
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    } else {
      return timestamp.toLocaleString('en-GB', { 
        hour12: false,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const getActionType = (type) => {
    const isCheckIn = type === 'checkIn';
    return (
      <span style={{
        color: isCheckIn ? '#059669' : '#dc2626',
        fontWeight: 600,
        fontSize: 14,
        padding: '4px 8px',
        borderRadius: '4px',
        backgroundColor: isCheckIn ? '#d1fae5' : '#fee2e2'
      }}>
        {isCheckIn ? 'Check In' : 'Check Out'}
      </span>
    );
  };

  const formatWorkHours = (hours) => {
    if (!hours || hours === 0) return '0h 0m';
    
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    
    if (wholeHours === 0) {
      return `${minutes}m`;
    } else {
      return `${wholeHours}h ${minutes}m`;
    }
  };

  // Helper function to format custom date range
  const formatCustomDateRange = (startDate, endDate) => {
    if (!startDate && !endDate) {
      return 'Custom Range';
    }
    
    const formatDate = (dateString) => {
      if (!dateString) return null;
      const date = new Date(dateString);
      const options = { month: 'short', day: 'numeric', year: 'numeric' };
      return date.toLocaleDateString('en-US', options);
    };
    
    const formattedStart = formatDate(startDate);
    const formattedEnd = formatDate(endDate);
    
    if (formattedStart && formattedEnd) {
      // If same year, only show year once
      const startYear = new Date(startDate).getFullYear();
      const endYear = new Date(endDate).getFullYear();
      
      if (startYear === endYear) {
        const startOptions = { month: 'short', day: 'numeric' };
        const endOptions = { month: 'short', day: 'numeric', year: 'numeric' };
        const shortStart = new Date(startDate).toLocaleDateString('en-US', startOptions);
        const fullEnd = new Date(endDate).toLocaleDateString('en-US', endOptions);
        return `${shortStart} - ${fullEnd}`;
      }
      return `${formattedStart} - ${formattedEnd}`;
    } else if (formattedStart) {
      return `From ${formattedStart}`;
    } else if (formattedEnd) {
      return `Until ${formattedEnd}`;
    }
    
    return 'Custom Range';
  };

  const getTimePeriodLabel = (period, customStartDate = null, customEndDate = null) => {
    switch (period) {
      case 'today': return 'Today';
      case 'week': return 'This Week';
      case 'month': return 'This Month';
      case 'custom': return formatCustomDateRange(customStartDate, customEndDate);
      default: return 'This Week';
    }
  };

  // CSV Download Functions using Firebase Function
  const downloadTeamCSV = async (team) => {
    try {
      const generateCSV = httpsCallable(functions, 'generateWorkHoursCSV');
      
      const result = await generateCSV({
        projectId,
        timePeriod: reportTimePeriod,
        teamId: team.teamId,
        includeAllTeams: false,
        customStartDate: reportTimePeriod === 'custom' ? customStartDate : null,
        customEndDate: reportTimePeriod === 'custom' ? customEndDate : null
      });
      
      const { csvContent, fileName } = result.data;
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('✅ CSV downloaded successfully');
    } catch (error) {
      console.error('❌ Error downloading CSV:', error);
      alert('Error generating CSV report. Please try again.');
    }
  };

  const downloadAllTeamsCSV = async () => {
    try {
      const generateCSV = httpsCallable(functions, 'generateWorkHoursCSV');
      
      const result = await generateCSV({
        projectId,
        timePeriod: reportTimePeriod,
        includeAllTeams: true,
        customStartDate: reportTimePeriod === 'custom' ? customStartDate : null,
        customEndDate: reportTimePeriod === 'custom' ? customEndDate : null
      });
      
      const { csvContent, fileName } = result.data;
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('✅ All teams CSV downloaded successfully');
    } catch (error) {
      console.error('❌ Error downloading CSV:', error);
      alert('Error generating CSV report. Please try again.');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        Loading attendance…
      </div>
    );
  }

  // Filter records based on selected team
  // Separate PM from team records for current view
  const pmCurrentRecords = attendanceRecords.filter(r => r.role === 'pm');
  const teamAttendanceRecords = attendanceRecords.filter(r => r.role !== 'pm');

  const displayRecords = selectedTeam
    ? teamAttendanceRecords.filter(record => record.team?.id === selectedTeam)
    : teamAttendanceRecords;
  
  // Group PMs by company
  const groupedPMsByCompany = {};
  pmCurrentRecords.forEach(pm => {
    const companyName = pmCompanies[pm.uid] || 'Unknown Company';
    if (!groupedPMsByCompany[companyName]) {
      groupedPMsByCompany[companyName] = [];
    }
    groupedPMsByCompany[companyName].push(pm);
  });
  
  // Sort companies alphabetically
  const sortedCompanyNames = Object.keys(groupedPMsByCompany).sort();

  // Separate PM from team records for historical view
  const pmHistoricalRecords = historicalRecords.filter(r => r.role === 'pm');
  const teamHistoricalRecords = historicalRecords.filter(r => r.role !== 'pm');

  const displayHistoricalRecords = selectedHistoricalTeam
    ? teamHistoricalRecords.filter(record => record.team?.id === selectedHistoricalTeam)
    : teamHistoricalRecords;

  return (
    <div style={{ padding: 40, backgroundColor: 'var(--branding-app-surface)', minHeight: '100vh' }}>
      {/* Back Button */}
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => navigate(`/project/${projectId}`)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--cc-text-muted)',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.color = 'var(--branding-app-primary)';
          }}
          onMouseLeave={(e) => {
            e.target.style.color = 'var(--cc-text-muted)';
          }}
        >
          <ArrowLeft size={20} />
          Back to project
        </button>
      </div>

      {/* Project Information */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 'clamp(20px, 2.2vw, 22px)', fontWeight: 600, color: 'var(--cc-text-primary)', marginBottom: 24 }}>
            {project?.name}
          </h1>
          {project?.projectCode && (
            <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 12, lineHeight: 1.4 }}>
              <strong>Project Code:</strong> {project.projectCode}
            </p>
          )}
          <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 16, lineHeight: 1.4 }}>
            <strong>Address:</strong> {project?.address}
          </p>
          <p style={{ fontSize: 'clamp(12px, 1.3vw, 14px)', color: 'var(--cc-text-secondary)', marginBottom: 24, lineHeight: 1.4 }}>
            <strong>Postcode:</strong> {project?.postcode}
          </p>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div style={{
        backgroundColor: 'var(--cc-card-bg)',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        padding: '24px',
        maxWidth: '1100px',
        margin: '0 auto 24px auto',
        width: '90%'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '16px'
        }}>
          <span style={{ fontWeight: 600, color: 'var(--cc-text-primary)' }}>View Mode:</span>
          <button
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              background: viewMode === 'current' ? 'var(--branding-app-primary)' : 'var(--branding-app-surface)',
              color: viewMode === 'current' ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-muted)',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '14px'
            }}
            onClick={() => setViewMode('current')}
          >
            Current Status
          </button>
          <button
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              background: viewMode === 'historical' ? 'var(--branding-app-primary)' : 'var(--branding-app-surface)',
              color: viewMode === 'historical' ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-muted)',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '14px'
            }}
            onClick={() => setViewMode('historical')}
          >
            Historical Records
          </button>
          <button
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              background: viewMode === 'teamReport' ? 'var(--branding-app-primary)' : 'var(--branding-app-surface)',
              color: viewMode === 'teamReport' ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-muted)',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '14px'
            }}
            onClick={() => setViewMode('teamReport')}
          >
            Team Report
          </button>
        </div>

        {/* Historical Date Filters */}
        {viewMode === 'historical' && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onClear={() => { setStartDate(''); setEndDate(''); }}
              placeholder="Select date range"
              style={{ minWidth: '200px' }}
            />
          </div>
        )}

        {/* Team Report Time Period Selector */}
        {viewMode === 'teamReport' && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span style={{ fontSize: '14px', color: 'var(--cc-text-primary)' }}>Time Period:</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                {['today', 'week', 'month', 'custom'].map(period => (
                  <button
                    key={period}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      background: reportTimePeriod === period ? 'var(--branding-app-primary)' : 'var(--branding-app-surface)',
                      color: reportTimePeriod === period ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-muted)',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 500
                    }}
                    onClick={() => setReportTimePeriod(period)}
                  >
                    {period === 'custom' ? 'Custom Range' : getTimePeriodLabel(period)}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Custom Date Range Inputs */}
            {reportTimePeriod === 'custom' && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                <DateRangePicker
                  startDate={customStartDate}
                  endDate={customEndDate}
                  onStartDateChange={setCustomStartDate}
                  onEndDateChange={setCustomEndDate}
                  onClear={() => { setCustomStartDate(''); setCustomEndDate(''); }}
                  placeholder="Select custom range"
                  style={{ minWidth: '200px' }}
                />
                <button
                  onClick={() => {
                    // Force a refresh of the team report data with custom dates
                    setRefreshTrigger(prev => prev + 1);
                  }}
                  style={{
                    padding: '6px 12px',
                    background: 'var(--branding-app-primary)',
                    color: 'var(--branding-app-primary-fg)',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Set
                </button>
                {(customStartDate || customEndDate) && (
                  <button
                    onClick={() => { setCustomStartDate(''); setCustomEndDate(''); }}
                    style={{
                      padding: '6px 12px',
                      background: 'var(--cc-cancel-bg)',
                      color: 'var(--cc-text-primary)',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Attendance Panel */}
      <div style={{
        backgroundColor: 'var(--cc-card-bg)',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        padding: '32px',
        maxWidth: '1100px',
        margin: '0 auto',
        width: '90%'
      }}>
        <div style={{
          marginBottom: '1rem',
          borderBottom: '1px solid var(--branding-app-surface-panel-border)',
          paddingBottom: '0.5rem',
          fontSize: '1.25rem',
          fontWeight: 600,
          color: 'var(--cc-text-primary)'
        }}>
          {viewMode === 'current' ? 'Current Team Attendance' :
           viewMode === 'historical' ? 'Historical Attendance Records' :
           'Team Work Hours Report'}
        </div>

        {/* Team Tabs */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1.5rem',
          overflowX: 'auto',
          padding: '0.5rem 0'
        }}>
          <button
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              background: (viewMode === 'current' ? !selectedTeam : !selectedHistoricalTeam) ? 'var(--branding-app-primary)' : 'var(--branding-app-surface)',
              color: (viewMode === 'current' ? !selectedTeam : !selectedHistoricalTeam) ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-muted)',
              cursor: 'pointer',
              border: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}
            onClick={() => {
              if (viewMode === 'current') setSelectedTeam(null);
              else setSelectedHistoricalTeam(null);
            }}
          >
            All teams
          </button>
          {teams.map(team => {
            const currentTeamCount = attendanceRecords.filter(record => record.team?.id === team.id).length;
            const historicalTeamCount = historicalRecords.filter(record => record.team?.id === team.id).length;
            const teamCount = viewMode === 'current' ? currentTeamCount : historicalTeamCount;
            
            return (
              <button
                key={team.id}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '0.5rem',
                  background: (viewMode === 'current' ? selectedTeam === team.id : selectedHistoricalTeam === team.id) ? 'var(--branding-app-primary)' : 'var(--branding-app-surface)',
                  color: (viewMode === 'current' ? selectedTeam === team.id : selectedHistoricalTeam === team.id) ? 'var(--branding-app-primary-fg)' : 'var(--cc-text-muted)',
                  cursor: 'pointer',
                  border: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  whiteSpace: 'nowrap'
                }}
                onClick={() => {
                  if (viewMode === 'current') setSelectedTeam(team.id);
                  else setSelectedHistoricalTeam(team.id);
                }}
              >
                {team.name} ({teamCount})
              </button>
            );
          })}
        </div>

        {viewMode === 'current' ? (
          // Current Status View (PMs grouped by company first, then Teams)
          <>
            <div ref={checkInPickerRef} style={{ marginBottom: '1.5rem', position: 'relative' }}>
              {!isReadOnly && (
              <button
                type="button"
                onClick={() => {
                  const opening = !showCheckInPicker;
                  setShowCheckInPicker(opening);
                  if (opening) loadCheckInRoster();
                }}
                style={{
                  padding: '8px 14px',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#059669',
                  background: '#d1fae5',
                  border: '1px solid #a7f3d0',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                + Check someone in
              </button>
              )}

              {showCheckInPicker && (
                <div style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  left: 0,
                  zIndex: 20,
                  width: '320px',
                  maxHeight: '360px',
                  overflowY: 'auto',
                  background: 'var(--branding-app-surface-panel, #fff)',
                  border: '1px solid var(--branding-app-surface-panel-border, #e2e8f0)',
                  borderRadius: '8px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  padding: '12px'
                }}>
                  <input
                    type="text"
                    autoFocus
                    placeholder="Search by name…"
                    value={checkInSearch}
                    onChange={(e) => setCheckInSearch(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      fontSize: '13px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      marginBottom: '10px',
                      boxSizing: 'border-box'
                    }}
                  />
                  {loadingRoster ? (
                    <div style={{ fontSize: '13px', color: 'var(--cc-text-muted)', padding: '8px 4px' }}>Loading…</div>
                  ) : checkInCandidates.length === 0 ? (
                    <div style={{ fontSize: '13px', color: 'var(--cc-text-muted)', padding: '8px 4px' }}>
                      {checkInSearch ? 'No matches' : 'Everyone is already checked in'}
                    </div>
                  ) : (
                    checkInCandidates.map(person => (
                      <button
                        key={person.uid}
                        type="button"
                        disabled={manualCheckLoading === person.uid}
                        onClick={async () => {
                          await handleManualCheck(person.uid, 'checkIn');
                          setShowCheckInPicker(false);
                          setCheckInSearch('');
                          setCheckInRoster(null); // force a fresh roster next time (this person is now on site)
                        }}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                          textAlign: 'left',
                          padding: '8px 6px',
                          fontSize: '13px',
                          background: 'none',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: manualCheckLoading === person.uid ? 'not-allowed' : 'pointer',
                          opacity: manualCheckLoading === person.uid ? 0.6 : 1
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--branding-app-surface, #f3f4f6)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                      >
                        <span style={{ color: 'var(--cc-text-primary)' }}>{person.name}</span>
                        <span style={{ color: 'var(--cc-text-muted)', fontSize: '12px' }}>{person.context}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {sortedCompanyNames.map(companyName => (
              <div key={companyName} style={{ marginBottom: '24px' }}>
                <div style={{
                  marginBottom: '1rem',
                  borderBottom: '1px solid var(--branding-app-surface-panel-border)',
                  paddingBottom: '0.5rem',
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: 'var(--cc-text-primary)'
                }}>
                  {companyName}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--branding-app-surface-panel-border)' }}>
                      <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-primary)' }}>Name</th>
                      <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-primary)' }}>Checked in at</th>
                      <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-primary)' }}>Override</th>
                      <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-primary)' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedPMsByCompany[companyName].map(record => (
                      <tr key={`pm-current-${record.uid}`} style={{
                        borderBottom: '1px solid var(--cc-badge-bg)',
                        backgroundColor: '#f0fdf4'
                      }}>
                        <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img
                              src={record.profilePictureUrl || '/crewchainDefault.png'}
                              alt={record.name}
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '6px',
                                marginRight: '0.75rem',
                                objectFit: 'cover',
                                backgroundColor: 'var(--branding-app-surface)',
                                cursor: 'pointer',
                                border: '2px solid var(--branding-app-surface-panel-border)',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.border = '2px solid var(--branding-app-primary)';
                                e.target.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.border = '2px solid var(--branding-app-surface-panel-border)';
                                e.target.style.transform = 'scale(1)';
                              }}
                              onClick={() => setProfileModal({
                                open: true,
                                url: record.profilePictureUrl || '/crewchainDefault.png',
                                name: record.name
                              })}
                            />
                            <span style={{ fontWeight: 500, color: 'var(--cc-text-primary)' }}>{record.name}</span>
                          </div>
                        </td>
                        <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle', color: 'var(--cc-text-muted)', fontSize: '14px' }}>
                          {formatTime(record.signInLog?.timestamp)}
                          {record.signInLog?.isOverride && (
                            <OverrideTooltip log={record.signInLog}>
                              <span style={{
                                marginLeft: '8px',
                                color: '#b45309',
                                fontSize: '12px',
                                fontWeight: 500,
                                cursor: 'help',
                                borderBottom: '1px dotted #b45309'
                              }}>
                                ?
                              </span>
                            </OverrideTooltip>
                          )}
                        </td>
                        <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle' }}>
                          {record.signInLog?.isOverride ? (
                            <OverrideTooltip log={record.signInLog}>
                              <span style={{
                                color: '#b45309',
                                fontWeight: 600,
                                cursor: 'help',
                                borderBottom: '1px dotted #b45309'
                              }}>
                                Yes
                              </span>
                            </OverrideTooltip>
                          ) : (
                            <span style={{
                              color: 'var(--cc-text-muted)',
                              fontWeight: 500
                            }}>
                              No
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle' }}>
                          {!isReadOnly && (
                          <button
                            onClick={() => handleManualCheck(record.uid, 'checkOut')}
                            disabled={manualCheckLoading === record.uid}
                            style={{
                              padding: '6px 12px',
                              fontSize: '13px',
                              fontWeight: 500,
                              color: '#dc2626',
                              background: '#fee2e2',
                              border: '1px solid #fecaca',
                              borderRadius: '6px',
                              cursor: manualCheckLoading === record.uid ? 'not-allowed' : 'pointer',
                              opacity: manualCheckLoading === record.uid ? 0.6 : 1
                            }}
                          >
                            {manualCheckLoading === record.uid ? '…' : 'Check Out'}
                          </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}

            <table style={{
              width: '100%',
              borderCollapse: 'collapse'
            }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--branding-app-surface-panel-border)' }}>
                  <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-primary)' }}>Name</th>
                  <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-secondary)' }}>Team</th>
                  <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-primary)' }}>Checked in at</th>
                  <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-primary)' }}>Override</th>
                  <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-primary)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayRecords.map(record => (
                <tr key={record.uid} style={{
                  borderBottom: '1px solid var(--cc-badge-bg)',
                  backgroundColor: '#f0fdf4'
                }}>
                  <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <img
                        src={record.profilePictureUrl || '/crewchainDefault.png'}
                        alt={record.name}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '6px',
                          marginRight: '0.75rem',
                          objectFit: 'cover',
                          backgroundColor: 'var(--branding-app-surface)',
                          cursor: 'pointer',
                          border: '2px solid #e2e8f0',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.border = '2px solid var(--branding-app-primary)';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.border = '2px solid #e2e8f0';
                          e.target.style.transform = 'scale(1)';
                        }}
                        onClick={() => setProfileModal({
                          open: true,
                          url: record.profilePictureUrl || '/crewchainDefault.png',
                          name: record.name
                        })}
                      />
                      <span style={{ fontWeight: 500, color: 'var(--cc-text-primary)' }}>{record.name}</span>
                    </div>
                  </td>
                  <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle', color: 'var(--cc-text-muted)' }}>
                    {record.team?.name || '-'}
                  </td>
                  <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle', color: 'var(--cc-text-muted)', fontSize: '14px' }}>
                    {formatTime(record.signInLog?.timestamp)}
                    {record.signInLog?.isOverride && (
                      <OverrideTooltip log={record.signInLog}>
                        <span style={{
                          marginLeft: '8px',
                          color: '#b45309',
                          fontSize: '12px',
                          fontWeight: 500,
                          cursor: 'help',
                          borderBottom: '1px dotted #b45309'
                        }}>
                          ?
                        </span>
                      </OverrideTooltip>
                    )}
                  </td>
                  <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle' }}>
                    {record.signInLog?.isOverride ? (
                      <OverrideTooltip log={record.signInLog}>
                        <span style={{
                          color: '#b45309',
                          fontWeight: 600,
                          cursor: 'help',
                          borderBottom: '1px dotted #b45309'
                        }}>
                          Yes
                        </span>
                      </OverrideTooltip>
                    ) : (
                      <span style={{
                        color: 'var(--cc-text-muted)',
                        fontWeight: 500
                      }}>
                        No
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle' }}>
                    {!isReadOnly && (
                    <button
                      onClick={() => handleManualCheck(record.uid, 'checkOut')}
                      disabled={manualCheckLoading === record.uid}
                      style={{
                        padding: '6px 12px',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#dc2626',
                        background: '#fee2e2',
                        border: '1px solid #fecaca',
                        borderRadius: '6px',
                        cursor: manualCheckLoading === record.uid ? 'not-allowed' : 'pointer',
                        opacity: manualCheckLoading === record.uid ? 0.6 : 1
                      }}
                    >
                      {manualCheckLoading === record.uid ? '…' : 'Check Out'}
                    </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </>
        ) : viewMode === 'historical' ? (
          // Historical Records Tables (PM first, then Teams)
          <>
            {pmHistoricalRecords.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  marginBottom: '1rem',
                  borderBottom: '1px solid var(--branding-app-surface-panel-border)',
                  paddingBottom: '0.5rem',
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: '#4c1d95'
                }}>
                  Project Managers (Historical)
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--branding-app-surface-panel-border)' }}>
                      <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-primary)' }}>Name</th>
                      <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-primary)' }}>Action</th>
                      <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-primary)' }}>Time</th>
                      <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-primary)' }}>Override</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pmHistoricalRecords.map(record => (
                      <tr key={`pmh-${record.uid}-${record.logId}`} style={{ 
                        borderBottom: '1px solid var(--cc-badge-bg)',
                        backgroundColor: record.type === 'checkIn' ? '#f0fdf4' : '#fef2f2'
                      }}>
                        <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle' }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img
                              src={record.profilePictureUrl || '/crewchainDefault.png'}
                              alt={record.name}
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '6px',
                                marginRight: '0.75rem',
                                objectFit: 'cover',
                                backgroundColor: 'var(--branding-app-surface)',
                                border: '2px solid var(--branding-app-surface-panel-border)'
                              }}
                            />
                            <span style={{ fontWeight: 500, color: 'var(--cc-text-primary)' }}>{record.name}</span>
                          </div>
                        </td>
                        <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle' }}>{getActionType(record.type)}</td>
                        <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle', color: 'var(--cc-text-muted)', fontSize: '14px' }}>
                          {formatTime(record.timestamp)}
                        </td>
                        <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle' }}>
                          <span style={{
                            color: record.isOverride ? '#b45309' : 'var(--cc-text-muted)',
                            fontWeight: 500
                          }}>
                            {record.isOverride ? 'Yes' : 'No'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <table style={{
              width: '100%',
              borderCollapse: 'collapse'
            }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--branding-app-surface-panel-border)' }}>
                <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-primary)' }}>Name</th>
                <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-secondary)' }}>Team</th>
                <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-primary)' }}>Action</th>
                <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-primary)' }}>Time</th>
                <th style={{ padding: '1rem 0.5rem', textAlign: 'left', fontWeight: 600, color: 'var(--cc-text-primary)' }}>Override</th>
              </tr>
            </thead>
            <tbody>
              {displayHistoricalRecords.map(record => (
                <tr key={`${record.uid}-${record.logId}`} style={{ 
                  borderBottom: '1px solid var(--cc-badge-bg)',
                  backgroundColor: record.type === 'checkIn' ? '#f0fdf4' : '#fef2f2'
                }}>
                  <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <img
                        src={record.profilePictureUrl || '/crewchainDefault.png'}
                        alt={record.name}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '6px',
                          marginRight: '0.75rem',
                          objectFit: 'cover',
                          backgroundColor: 'var(--branding-app-surface)',
                          cursor: 'pointer',
                          border: '2px solid #e2e8f0',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.border = '2px solid var(--branding-app-primary)';
                          e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.border = '2px solid #e2e8f0';
                          e.target.style.transform = 'scale(1)';
                        }}
                        onClick={() => setProfileModal({ 
                          open: true, 
                          url: record.profilePictureUrl || '/crewchainDefault.png', 
                          name: record.name 
                        })}
                      />
                      <span style={{ fontWeight: 500, color: 'var(--cc-text-primary)' }}>{record.name}</span>
                    </div>
                  </td>
                  <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle', color: 'var(--cc-text-muted)' }}>
                    {record.team?.name || '-'}
                  </td>
                  <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle' }}>
                    {getActionType(record.type)}
                  </td>
                  <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle', color: 'var(--cc-text-muted)', fontSize: '14px' }}>
                    {formatTime(record.timestamp)}
                  </td>
                  <td style={{ padding: '1rem 0.5rem', verticalAlign: 'middle' }}>
                    <span style={{
                      color: record.isOverride ? '#b45309' : 'var(--cc-text-muted)',
                      fontWeight: 500
                    }}>
                      {record.isOverride ? 'Yes' : 'No'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
          </>
        ) : (
          // Team Report View
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Summary Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                background: 'var(--branding-app-surface-panel)',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid var(--branding-app-surface-panel-border)'
              }}>
                <div style={{ fontSize: '14px', color: 'var(--cc-text-muted)', marginBottom: '8px' }}>
                  Total Hours ({getTimePeriodLabel(reportTimePeriod, customStartDate, customEndDate)})
                </div>
                <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--cc-text-primary)' }}>
                  {formatWorkHours(teamReportData.reduce((sum, team) => sum + team.totalHours, 0))}
                </div>
              </div>
              <div style={{
                background: 'var(--branding-app-surface-panel)',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid var(--branding-app-surface-panel-border)'
              }}>
                <div style={{ fontSize: '14px', color: 'var(--cc-text-muted)', marginBottom: '8px' }}>
                  Total Sessions
                </div>
                <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--cc-text-primary)' }}>
                  {teamReportData.reduce((sum, team) => sum + team.totalSessions, 0)}
                </div>
              </div>
              <div style={{
                background: 'var(--branding-app-surface-panel)',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid var(--branding-app-surface-panel-border)'
              }}>
                <div style={{ fontSize: '14px', color: 'var(--cc-text-muted)', marginBottom: '8px' }}>
                  Active Teams
                </div>
                <div style={{ fontSize: '24px', fontWeight: '600', color: 'var(--cc-text-primary)' }}>
                  {teamReportData.filter(team => team.totalHours > 0).length}
                </div>
              </div>
            </div>

            {/* Download All Teams Button */}
            {teamReportData.length > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: '16px'
              }}>
                <button
                  onClick={downloadAllTeamsCSV}
                  style={{
                    padding: '10px 20px',
                    background: 'var(--branding-app-primary)',
                    color: 'var(--branding-app-primary-fg)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'var(--branding-app-primary-hover)';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'var(--branding-app-primary)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Download All Teams CSV
                </button>
              </div>
            )}

            {/* Team Cards */}
            {teamReportData.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: 'var(--cc-text-muted)',
                fontSize: '16px'
              }}>
                No work hours data found for {getTimePeriodLabel(reportTimePeriod, customStartDate, customEndDate).toLowerCase()}.
              </div>
            ) : (
              teamReportData.map(team => (
                <div
                  key={team.teamId}
                  style={{
                    background: 'var(--cc-card-bg)',
                    borderRadius: '12px',
                    border: '1px solid var(--branding-app-surface-panel-border)',
                    overflow: 'hidden',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  {/* Team Header */}
                  <div style={{
                    padding: '20px',
                    borderBottom: '1px solid var(--branding-app-surface)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: team.teamColor,
                        border: '2px solid var(--cc-card-bg)',
                        boxShadow: '0 0 0 2px var(--branding-app-surface-panel-border)'
                      }} />
                      <div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cc-text-primary)' }}>
                          {team.teamName}
                        </div>
                        <div style={{ fontSize: '14px', color: 'var(--cc-text-muted)' }}>
                          {team.memberCount} member{team.memberCount !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '20px', fontWeight: '600', color: 'var(--cc-text-primary)' }}>
                        {formatWorkHours(team.totalHours)}
                      </div>
                      <div style={{ fontSize: '14px', color: 'var(--cc-text-muted)' }}>
                        {team.totalSessions} session{team.totalSessions !== 1 ? 's' : ''}
                        {team.totalOverrideSessions > 0 && (
                          <span style={{ color: '#b45309', marginLeft: '8px' }}>
                            ({team.totalOverrideSessions} override{team.totalOverrideSessions !== 1 ? 's' : ''})
                          </span>
                        )}
                      </div>
                      {/* Team Download Button */}
                      <button
                        onClick={() => downloadTeamCSV(team)}
                        style={{
                          marginTop: '12px',
                          padding: '8px 16px',
                          background: 'var(--branding-app-primary)',
                          color: 'var(--branding-app-primary-fg)',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: '14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'var(--branding-app-primary-hover)';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'var(--branding-app-primary)';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                        }}
                      >
                        Download
                      </button>
                    </div>
                  </div>

                  {/* Team Members */}
                  {team.memberDetails.length > 0 && (
                    <div style={{ padding: '0 20px 20px 20px' }}>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--cc-text-primary)', marginBottom: '12px' }}>
                        Team Members
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {team.memberDetails.map(member => (
                          <div
                            key={member.userId}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '12px',
                              background: 'var(--branding-app-surface-panel)',
                              borderRadius: '8px',
                              border: '1px solid var(--branding-app-surface-panel-border)'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <img
                                src={member.profilePictureUrl || '/crewchainDefault.png'}
                                alt={member.name}
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  border: '1px solid var(--branding-app-surface-panel-border)'
                                }}
                              />
                              <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--cc-text-primary)' }}>
                                {member.name}
                              </span>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cc-text-primary)' }}>
                                {formatWorkHours(member.totalHours)}
                              </div>
                              <div style={{ fontSize: '12px', color: 'var(--cc-text-muted)' }}>
                                {member.totalSessions} session{member.totalSessions !== 1 ? 's' : ''}
                                {member.totalOverrideSessions > 0 && (
                                  <span style={{ color: '#b45309', marginLeft: '4px' }}>
                                    ({member.totalOverrideSessions} override{member.totalOverrideSessions !== 1 ? 's' : ''})
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {viewMode === 'historical' && displayHistoricalRecords.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            color: 'var(--cc-text-muted)',
            fontSize: '16px'
          }}>
            No historical records found for the selected date range and team.
          </div>
        )}
      </div>

      {/* Profile Modal */}
      <ImageModal
        open={profileModal.open}
        onClose={() => setProfileModal({ open: false, url: null, name: null })}
        imageUrl={profileModal.url}
        name={profileModal.name}
      />
    </div>
  );
}

export default withProjectPermissions(AttendanceLanding, { requireEdit: true });
